import appState from 'snippets/search-bar/@store'
import { createContext } from 'preact'
import { useContext, useEffect, useMemo, useState } from 'preact/hooks'
import SearchDropdown from 'snippets/search-dropdown/search-dropdown'
import { selectAll, removeClass, addClass, select } from 'helpers/dom'
import { clearAllBodyScrollLocks } from 'body-scroll-lock'
import Icon from 'snippets/icon/icon'
import debounce from 'lodash.debounce'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'

let devtools
if (process.env.NODE_ENV === 'development') {
  devtools = require('preact/devtools')
}

const selectors = {
  SEARCH_BAR_SELECTOR: '.js-search-bar',
  SEARCH_BAR_OPENED_CLASS: 'is-search-opened',
  SITE_HEADER_CLASS: '.js-site-header',
  BACKGROUND_CLASS: 'bg-white'
}

const AppState = createContext()

function SearchBar(props) {
  return (
    <AppState.Provider value={appState()}>
      <App slot={props} />
    </AppState.Provider>
  )
}

function App(props) {
  const state = useContext(AppState)

  const originalQuery = useMemo(() => state.originalQuery.value, [state.originalQuery.value])
  const isOpenDropdown = useMemo(() => state.isOpenDropdown.value, [state.isOpenDropdown.value])

  const [isInputActive, setIsInputActive] = useState(false)

  const wordsToType = GM_STATE?.integrations?.searchSpringConfig?.phrasesDefault?.length > 0
    ? GM_STATE.integrations.searchSpringConfig.phrasesDefault
    : ['Bedside Table', 'Baby cot ', 'Entertainment Unit ', 'Coffee table ', 'Arm Chair', 'Furniture Packages', 'Nursery Furniture']
  const [wordIndex, setWordIndex] = useState(0)
  const [currentWord, setCurrentWord] = useState('What are you looking for?')
  const [isDeleting, setIsDeleting] = useState(false)
  const [typingSpeed, setTypingSpeed] = useState(1000)

  const searchUrl = useMemo(() => {
    let newUrl = `/search?q=${encodeURIComponent(state.originalQuery.value)}`
    if (state.searchValue.value && state.searchValue.value !== state.originalQuery.value) {
      newUrl = `/search?q=${encodeURIComponent(state.searchValue.value)}&oq=${encodeURIComponent(state.originalQuery.value)}&spell=1`
    }
    if (state.searchValue.value && state.correctedQuery.value && state.correctedQuery.value !== state.searchValue.value) {
      newUrl = `/search?q=${encodeURIComponent(state.correctedQuery.value)}&oq=${encodeURIComponent(state.searchValue.value)}&spell=1`
    }
    return newUrl
  }, [state.originalQuery.value, state.searchValue.value, state.correctedQuery.value])

  const searchBarEl = selectAll(selectors.SEARCH_BAR_SELECTOR)
  const siteHeaderEl = select(selectors.SITE_HEADER_CLASS)

  const onCloseDropdown = () => {
    removeClass(selectors.BACKGROUND_CLASS, siteHeaderEl)
    state.closeDropdown()
    clearAllBodyScrollLocks()
  }

  const setSearchState = (e) => {
    const currentValue = e.target.value
    state.setOriginalQuery(currentValue)
    state.setSearchValue(currentValue)
    state.setCorrectedQuery(currentValue)
  }

  const debouncedSearch = debounce((e) => {
    const currentValue = e.target.value
    if (currentValue.length > 0) {
      addClass(selectors.BACKGROUND_CLASS, siteHeaderEl)
      state.getSuggestion(currentValue)
      state.setIsLoading(true)
    } else {
      state.setIsTrendingActive(true)
      state.setIsLoading(false)
    }
  }, 500)

  const closeSearch = () => {
    state.setOriginalQuery(null)
    onCloseDropdown()
    searchBarEl.forEach((el) => {
      removeClass(selectors.SEARCH_BAR_OPENED_CLASS, el)
    })
  }

  const onSearch = (e) => {
    e.preventDefault()
    window.location.href = searchUrl
  }

  const onFocus = (e) => {
    addClass(selectors.BACKGROUND_CLASS, siteHeaderEl)
    setIsInputActive(true)
    // MCK0002SD-145 temporarily disabled for launch
    // setCurrentWord('')

    if (e.target.value === '') {
      state.setIsOpenDropdown(true)
      state.setIsTrendingActive(true)
    }
  }

  const onBlur = () => {
    removeClass(selectors.BACKGROUND_CLASS, siteHeaderEl)
  }

  useEffect(() => {
    state.getTrending()

    const handleUnlock = () => {
      closeSearch()
    }

    globalEmitter.on(emitterProps.search.closeDropdown, handleUnlock)

    return () => {
      globalEmitter.off(emitterProps.search.closeDropdown, handleUnlock)
    }
  }, [])

  // MCK0002SD-145 temporarily disabled for launch
  // useEffect(() => {
  //   const type = () => {
  //     const word = wordsToType[wordIndex]
  //     // isInputActive is UI state, word, currentWord are required data
  //     if (isInputActive || !word) {
  //       return
  //     }

  //     if (isDeleting) {
  //       setCurrentWord((prevWord) => prevWord?.substring(0, prevWord.length - 1))
  //       if (currentWord === '') {
  //         setIsDeleting(false)
  //         setWordIndex((prevIndex) => (prevIndex + 1) % wordsToType.length)
  //         setTypingSpeed(200)
  //       }
  //     } else {
  //       const currentCharacter = word[currentWord.length] ? word[currentWord.length] : ''
  //       setCurrentWord((prevWord) => prevWord + currentCharacter)
  //       if (currentWord === word) {
  //         setIsDeleting(true)
  //         setTypingSpeed(200)
  //       }
  //     }
  //   }

  //   const timeout = setTimeout(() => {
  //     type()
  //   }, typingSpeed)

  //   return () => clearTimeout(timeout)
  // }, [currentWord, isDeleting, wordIndex, typingSpeed, wordsToType])

  // useEffect(() => {
  //   setTimeout(() => {
  //     setCurrentWord('')
  //   }, typingSpeed)
  //   setIsDeleting(false)
  //   setTypingSpeed(100)
  // }, [wordIndex])

  return (
    <>
      <form
        action={searchUrl}
        className="input-form flex h-12 items-center border-b-2 border-primary px-1 py-2 text-center lg:absolute lg:left-1/2 lg:w-[464px] lg:-translate-x-1/2 lg:-translate-y-1/2 xl:w-[724px] xl:-translate-x-[calc(50%+6px)]"
        onSubmit={onSearch}
      >
        <button type="submit" title="Search" aria-label="Search">
          <Icon name="icon-search" viewBox="0 0 24 24" className="mr-2 h-6 w-6 text-grey-1000" />
        </button>
        <input
          id={props?.slot?.inputId ? props?.slot?.inputId : 'searchbar'}
          type="text"
          placeholder="What are you looking for?"
          class="js-search-bar-input h-full flex-1 border-none bg-transparent px-0 text-sm font-medium text-grey-700 !ring-0 placeholder:text-grey-700"
          value={originalQuery}
          autocomplete="off"
          onInput={(e) => { setSearchState(e); debouncedSearch(e) }}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        <button type="button" title="Search close" className={`hidden ${isOpenDropdown && '!block'} search-bar__close`} onClick={closeSearch} aria-label="search close">
          <Icon name="icon-close-bold" viewBox="0 0 24 24" className="h-6 w-6 text-grey-1000" />
        </button>
      </form>
      {isOpenDropdown && <SearchDropdown state={state} closeSearch={closeSearch}></SearchDropdown>}
    </>
  )
}

export default SearchBar
